import {FormField} from 'argo-ui';
import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import {Form, Text} from 'argo-ui';
import {RouteComponentProps} from 'react-router';
import {AuthSettings} from '../../shared/models';
import {services} from '../../shared/services';
import {Context} from '../../shared/context';

require('./login.scss');

export interface LoginForm {
    username: string;
    password: string;
}

export function Login(props: RouteComponentProps<{}>) {
    const appContext = useContext(Context);

    const search = new URLSearchParams(props.history.location.search);
    const returnUrl = search.get('return_url') || '';
    const hasSsoLoginError = search.get('has_sso_error') === 'true';

    const [authSettings, setAuthSettings] = useState<AuthSettings | null>(null);
    const [loginError, setLoginError] = useState<string | null>(null);
    const [loginInProgress, setLoginInProgress] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            const authSettings = await services.authService.settings();
            setAuthSettings(authSettings);
        })();
    }, []);

    const login = async (username: string, password: string, returnURL: string) => {
        try {
            setLoginError('');
            setLoginInProgress(true);
            appContext.navigation.goto('.', {sso_error: null});

            await services.users.login(username.trim().replace(/@sophistec\.global$/i, ''), password);

            let redirectPath = '/applications';

            if (returnURL) {
                try {
                    let url: URL;
                    if (returnURL.startsWith('http://') || returnURL.startsWith('https://')) {
                        url = new URL(returnURL);
                    } else {
                        url = new URL(returnURL, window.location.origin);
                    }
                    redirectPath = url.pathname + url.search;
                    if (appContext.baseHref != '/' && redirectPath.startsWith(appContext.baseHref)) {
                        redirectPath = redirectPath.substring(appContext.baseHref.length);
                    }
                } catch (e) {
                    console.error('Failed to parse return URL:', e, 'returnURL:', returnURL);
                }
            }

            appContext.navigation.goto(redirectPath, undefined, {replace: true});
        } catch (e: any) {
            const errorMessage = e?.response?.body?.error || e?.response?.data?.error || e?.message || 'Login failed';
            setLoginError(errorMessage);
        } finally {
            setLoginInProgress(false);
        }
    };

    const ssoConfigured = authSettings && ((authSettings.dexConfig && (authSettings.dexConfig.connectors || []).length > 0) || authSettings.oidcConfig);

    return (
        <div className='login'>
            <aside className='login__brand' aria-hidden='true'>
                <img className='login__brand-logo' src='assets/sophistec/logo-inverted.png' alt='' />
                <div className='login__brand-footer'>© {new Date().getFullYear()} Sophistec Global</div>
            </aside>
            <main className='login__panel'>
                <div className='login__box'>
                    <img className='login__mark' src='assets/sophistec/mark.png' alt='Sophistec' />
                    <h2 className='login__title'>Sign in to Sophistec</h2>
                    {ssoConfigured && (
                        <div className='login__sso'>
                            <a href={`auth/login?return_url=${encodeURIComponent(returnUrl)}`} className='login__sso-button'>
                                <i className='fa fa-right-to-bracket' aria-hidden='true' />
                                {(authSettings.oidcConfig && <span>Continue with {authSettings.oidcConfig.name}</span>) ||
                                    (authSettings.dexConfig.connectors.length === 1 && <span>Continue with {authSettings.dexConfig.connectors[0].name}</span>) || (
                                        <span>Continue with SSO</span>
                                    )}
                            </a>
                            {hasSsoLoginError && (
                                <div className='login__error' role='alert'>
                                    SSO login failed. Try again or contact your administrator.
                                </div>
                            )}
                            {authSettings && !authSettings.userLoginsDisabled && (
                                <div className='login__separator'>
                                    <span>or sign in with a password</span>
                                </div>
                            )}
                        </div>
                    )}
                    {!authSettings && (
                        <div className='login__loading' role='status'>
                            <i className='fa fa-circle-notch fa-spin' aria-hidden='true' /> Loading sign-in options…
                        </div>
                    )}
                    {authSettings && !authSettings.userLoginsDisabled && (
                        <Form
                            onSubmit={(params: LoginForm) => login(params.username, params.password, returnUrl)}
                            onSubmitFailure={() => {}}
                            validateError={(params: LoginForm) => ({
                                username: !params.username && 'Username is required',
                                password: !params.password && 'Password is required'
                            })}>
                            {formApi => (
                                <form role='form' className='login__form' onSubmit={formApi.submitForm}>
                                    <div className='argo-form-row'>
                                        <FormField
                                            formApi={formApi}
                                            label='Username or email'
                                            field='username'
                                            component={Text}
                                            componentProps={{name: 'username', autoCapitalize: 'none', autoComplete: 'username', autoFocus: true}}
                                        />
                                    </div>
                                    <div className='argo-form-row'>
                                        <FormField
                                            formApi={formApi}
                                            label='Password'
                                            field='password'
                                            component={Text}
                                            componentProps={{name: 'password', type: 'password', autoComplete: 'current-password'}}
                                        />
                                    </div>
                                    {loginError && (
                                        <div className='login__error' role='alert'>
                                            {loginError}
                                        </div>
                                    )}
                                    <button disabled={loginInProgress} className='login__submit' type='submit'>
                                        {loginInProgress ? (
                                            <>
                                                <i className='fa fa-circle-notch fa-spin' aria-hidden='true' /> Signing in…
                                            </>
                                        ) : (
                                            'Sign in'
                                        )}
                                    </button>
                                </form>
                            )}
                        </Form>
                    )}
                    {authSettings && authSettings.userLoginsDisabled && !ssoConfigured && (
                        <div className='login__error' role='alert'>
                            Login is disabled. Please contact your system administrator.
                        </div>
                    )}
                </div>
                <div className='login__panel-footer'>
                    <a href='https://sophistec.global' target='_blank' rel='noopener noreferrer'>
                        sophistec.global
                    </a>
                </div>
            </main>
        </div>
    );
}
